/**
 * Animation Optimization Utilities
 * Provides utilities for performance-optimized animations
 */

/**
 * Check if user prefers reduced motion
 */
export const prefersReducedMotion = (): boolean => {
  if (typeof window === "undefined") return false;
  
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

/**
 * Check if device is low-end (mobile or slow CPU)
 */
export const isLowEndDevice = (): boolean => {
  if (typeof window === "undefined" || !("navigator" in window)) return false;

  // Check for mobile device
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );

  // Check for hardware concurrency (CPU cores)
  const cpuCores = navigator.hardwareConcurrency || 2;
  const isSlowCPU = cpuCores <= 2;

  // Check for device memory (if available)
  const deviceMemory = (navigator as any).deviceMemory;
  const isLowMemory = deviceMemory && deviceMemory <= 4;

  return isMobile || isSlowCPU || isLowMemory;
};

/**
 * Get animation configuration based on device capabilities
 */
export const getAnimationConfig = () => {
  const reducedMotion = prefersReducedMotion();
  const lowEnd = isLowEndDevice();

  return {
    // Disable animations if user prefers reduced motion
    enabled: !reducedMotion,
    
    // Reduce animation complexity on low-end devices
    complexity: lowEnd ? "simple" : "full",
    
    // Adjust animation duration
    duration: reducedMotion ? 0 : lowEnd ? 200 : 300,
    
    // Adjust stagger delay
    staggerDelay: reducedMotion ? 0 : lowEnd ? 50 : 100,
    
    // Enable/disable parallax effects
    parallax: !reducedMotion && !lowEnd,
    
    // Enable/disable blur effects
    blur: !lowEnd,
    
    // Enable/disable scale transforms
    scale: !reducedMotion,
  };
};

/**
 * Apply will-change property for optimized animations
 */
export const applyWillChange = (
  element: HTMLElement,
  properties: string[]
): void => {
  if (!element) return;
  
  element.style.willChange = properties.join(", ");
  
  // Remove will-change after animation completes to free up resources
  const removeWillChange = () => {
    element.style.willChange = "auto";
    element.removeEventListener("transitionend", removeWillChange);
    element.removeEventListener("animationend", removeWillChange);
  };
  
  element.addEventListener("transitionend", removeWillChange);
  element.addEventListener("animationend", removeWillChange);
};

/**
 * Optimize animation frame rate
 */
export const requestOptimizedAnimationFrame = (
  callback: FrameRequestCallback
): number => {
  if (typeof window === "undefined") return 0;
  
  const config = getAnimationConfig();
  
  if (!config.enabled) {
    // Execute immediately if animations are disabled
    callback(performance.now());
    return 0;
  }
  
  return requestAnimationFrame(callback);
};

/**
 * Throttle animation updates for better performance
 */
export const throttleAnimation = (
  callback: () => void,
  delay: number = 16
): (() => void) => {
  let lastCall = 0;
  let timeoutId: NodeJS.Timeout | null = null;
  
  return () => {
    const now = Date.now();
    const timeSinceLastCall = now - lastCall;
    
    if (timeSinceLastCall >= delay) {
      lastCall = now;
      callback();
    } else {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        lastCall = Date.now();
        callback();
      }, delay - timeSinceLastCall);
    }
  };
};

/**
 * CSS transform utilities for GPU-accelerated animations
 */
export const createTransform = (options: {
  x?: number;
  y?: number;
  scale?: number;
  rotate?: number;
}): string => {
  const transforms: string[] = [];
  
  if (options.x !== undefined || options.y !== undefined) {
    transforms.push(`translate3d(${options.x || 0}px, ${options.y || 0}px, 0)`);
  }
  
  if (options.scale !== undefined) {
    transforms.push(`scale(${options.scale})`);
  }
  
  if (options.rotate !== undefined) {
    transforms.push(`rotate(${options.rotate}deg)`);
  }
  
  return transforms.join(" ");
};

/**
 * Framer Motion variants optimized for performance
 */
export const getOptimizedVariants = () => {
  const config = getAnimationConfig();
  
  return {
    // Fade in animation
    fadeIn: {
      hidden: { 
        opacity: 0,
        y: config.enabled ? 20 : 0,
      },
      visible: { 
        opacity: 1,
        y: 0,
        transition: {
          duration: config.duration / 1000,
          ease: "easeOut",
        },
      },
    },
    
    // Scale animation
    scale: {
      rest: { scale: 1 },
      hover: config.scale ? { 
        scale: 1.05,
        transition: {
          duration: config.duration / 1000,
          ease: "easeInOut",
        },
      } : { scale: 1 },
    },
    
    // Slide in animation
    slideIn: {
      hidden: { 
        x: config.enabled ? -20 : 0,
        opacity: 0,
      },
      visible: { 
        x: 0,
        opacity: 1,
        transition: {
          duration: config.duration / 1000,
          ease: "easeOut",
        },
      },
    },
    
    // Stagger children animation
    staggerContainer: {
      hidden: { opacity: config.enabled ? 0 : 1 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: config.staggerDelay / 1000,
          delayChildren: 0.1,
        },
      },
    },
    
    // Stagger item animation
    staggerItem: {
      hidden: { 
        opacity: 0,
        y: config.enabled ? 10 : 0,
      },
      visible: { 
        opacity: 1,
        y: 0,
        transition: {
          duration: config.duration / 1000,
        },
      },
    },
  };
};

/**
 * Create intersection observer for scroll-triggered animations
 */
export const createScrollObserver = (
  callback: (entry: IntersectionObserverEntry) => void,
  options: IntersectionObserverInit = {}
): IntersectionObserver | null => {
  if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
    return null;
  }
  
  const config = getAnimationConfig();
  
  return new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          callback(entry);
        }
      });
    },
    {
      threshold: config.complexity === "simple" ? 0.1 : 0.2,
      rootMargin: "0px 0px -10% 0px",
      ...options,
    }
  );
};

/**
 * Optimize CSS for animations
 */
export const getOptimizedAnimationCSS = (): string => {
  const config = getAnimationConfig();
  
  if (!config.enabled) {
    return `
      * {
        animation-duration: 0s !important;
        transition-duration: 0s !important;
      }
    `;
  }
  
  return `
    .animate-optimized {
      will-change: transform, opacity;
      transform: translateZ(0);
      backface-visibility: hidden;
      perspective: 1000px;
    }
    
    .animate-optimized-hover {
      transition: transform ${config.duration}ms ease-out;
    }
    
    .animate-optimized-hover:hover {
      transform: ${config.scale ? "scale(1.05)" : "none"} translateZ(0);
    }
    
    @media (prefers-reduced-motion: reduce) {
      * {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
      }
    }
  `;
};

/**
 * Inject optimized animation CSS into document
 */
export const injectOptimizedAnimationCSS = (): void => {
  if (typeof document === "undefined") return;
  
  const styleId = "optimized-animations";
  
  // Remove existing style if present
  const existingStyle = document.getElementById(styleId);
  if (existingStyle) {
    existingStyle.remove();
  }
  
  // Create and inject new style
  const style = document.createElement("style");
  style.id = styleId;
  style.textContent = getOptimizedAnimationCSS();
  document.head.appendChild(style);
};

/**
 * Initialize animation optimizations
 */
export const initAnimationOptimizations = (): void => {
  if (typeof window === "undefined") return;
  
  // Inject optimized CSS
  injectOptimizedAnimationCSS();
  
  // Listen for reduced motion preference changes
  const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  mediaQuery.addEventListener("change", () => {
    injectOptimizedAnimationCSS();
  });
};
